import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";

const SITE_URL = "https://platform.treasury.sa";

const pageMetadata = {
  "/": {
    en: { title: "Treasury | Saudi Real Estate Investment Platform", description: "Explore fractional real estate investment opportunities and property-unit trading through Treasury." },
    ar: { title: "تريجري | منصة الاستثمار العقاري", description: "استكشف فرص الاستثمار العقاري الجزئي وتداول الوحدات العقارية عبر منصة تريجري." },
  },
  "/about": {
    en: { title: "About Treasury | Saudi Real Estate Investment Platform", description: "Learn about Treasury and its investor-facing platform for fractional real estate opportunities in Saudi Arabia." },
    ar: { title: "عن تريجري | منصة الاستثمار العقاري", description: "تعرّف على تريجري ومنصتها الموجهة للمستثمرين في فرص الملكية العقارية الجزئية في المملكة العربية السعودية." },
  },
  "/howitwork": {
    en: { title: "How Treasury Works | Fractional Real Estate Investment", description: "Understand the public investor journey for exploring, subscribing to, and trading real estate units on Treasury." },
    ar: { title: "كيف تعمل تريجري | الاستثمار العقاري الجزئي", description: "تعرّف على رحلة المستثمر لاستكشاف الفرص العقارية والاشتراك فيها وتداول الوحدات عبر تريجري." },
  },
  "/blog": {
    en: { title: "Treasury News | Real Estate Investment Updates", description: "Read public news and updates from Treasury." },
    ar: { title: "أخبار تريجري | مستجدات الاستثمار العقاري", description: "اطلع على الأخبار والمستجدات العامة من تريجري." },
  },
  "/contactus": {
    en: { title: "Contact Treasury | Real Estate Investment Platform", description: "Contact Treasury for partnership and platform enquiries." },
    ar: { title: "تواصل مع تريجري | منصة الاستثمار العقاري", description: "تواصل مع تريجري للاستفسارات المتعلقة بالشراكات والمنصة." },
  },
  "/termsofuse": {
    en: { title: "Treasury Terms of Use", description: "Review the terms governing use of the Treasury platform." },
    ar: { title: "شروط استخدام تريجري", description: "راجع الشروط التي تحكم استخدام منصة تريجري." },
  },
  "/privacypolicy": {
    en: { title: "Treasury Privacy Policy", description: "Review Treasury's privacy information." },
    ar: { title: "سياسة خصوصية تريجري", description: "راجع معلومات الخصوصية الخاصة بمنصة تريجري." },
  },
};

const privateRoute = (pathname) => /^(\/dashboard\/|\/admin\/|\/login$|\/register$|\/forgotPassword$|\/invoice\/)/.test(pathname);

const upsertMeta = (selector, attribute, value, content) => {
  let element = document.head.querySelector(selector);
  if (!element) {
    element = document.createElement("meta");
    element.setAttribute(attribute, value);
    document.head.appendChild(element);
  }
  element.setAttribute("content", content);
};

export default function SeoManager() {
  const location = useLocation();
  const { i18n } = useTranslation();

  useEffect(() => {
    const language = i18n.language === "ar" ? "ar" : "en";
    const privatePage = privateRoute(location.pathname);
    const metadata = pageMetadata[location.pathname]?.[language] || pageMetadata["/"][language];
    const canonicalUrl = `${SITE_URL}${location.pathname}`;

    document.title = metadata.title;
    upsertMeta('meta[name="description"]', "name", "description", metadata.description);
    upsertMeta('meta[name="robots"]', "name", "robots", privatePage ? "noindex,nofollow,noarchive" : "index,follow,max-image-preview:large,max-snippet:-1,max-video-preview:-1");
    upsertMeta('meta[property="og:title"]', "property", "og:title", metadata.title);
    upsertMeta('meta[property="og:description"]', "property", "og:description", metadata.description);
    upsertMeta('meta[property="og:url"]', "property", "og:url", canonicalUrl);
    upsertMeta('meta[name="twitter:title"]', "name", "twitter:title", metadata.title);
    upsertMeta('meta[name="twitter:description"]', "name", "twitter:description", metadata.description);

    let canonical = document.head.querySelector('link[rel="canonical"]');
    if (!canonical) {
      canonical = document.createElement("link");
      canonical.setAttribute("rel", "canonical");
      document.head.appendChild(canonical);
    }
    canonical.setAttribute("href", canonicalUrl);

    const schemaId = "treasury-route-schema";
    const existingSchema = document.getElementById(schemaId);
    if (privatePage) {
      if (existingSchema) existingSchema.remove();
      return;
    }

    const schema = {
      "@context": "https://schema.org",
      "@type": "WebPage",
      "@id": `${canonicalUrl}#webpage`,
      url: canonicalUrl,
      name: metadata.title,
      description: metadata.description,
      inLanguage: language === "ar" ? "ar-SA" : "en-SA",
      isPartOf: { "@id": `${SITE_URL}/#website` },
      about: { "@id": `${SITE_URL}/#organization` },
    };
    const script = existingSchema || document.createElement("script");
    script.id = schemaId;
    script.type = "application/ld+json";
    script.textContent = JSON.stringify(schema);
    if (!existingSchema) document.head.appendChild(script);
  }, [i18n.language, location.pathname]);

  return null;
}
